// Advertiser-space strings (merchant dashboard/editor, account pages), merged into i18n resources.
const en = {
  common: { saved: "Saved", winning: "Winning", losing: "Losing" },
  auth: {
    welcome_toast: "Welcome, {{name}}",
    forgot_link: "Forgot password?",
    forgot_title: "Forgot your password?",
    forgot_subtitle: "Enter your login email. We'll send you a link to choose a new password.",
    forgot_submit: "Send the link",
    forgot_sending: "Sending…",
    forgot_sent_title: "Check your inbox",
    forgot_sent_body: "If an account exists for {{email}}, a reset link is on its way. It is valid for 1 hour.",
    back_to_login: "Back to login",
    reset_title: "Choose a new password",
    reset_subtitle: "At least 8 characters. You'll use it to log in to your QRISE space.",
    new_password: "New password",
    confirm_password: "Confirm password",
    reset_submit: "Save my new password",
    reset_saving: "Saving…",
    reset_done_title: "Password updated",
    reset_done_body: "You can now log in with your new password.",
    go_to_login: "Log in",
    password_mismatch: "Passwords don't match.",
    reset_invalid_title: "Link invalid or expired",
    reset_invalid_body: "This reset link has already been used or has expired. Request a new one below.",
    request_new_link: "Request a new link",
    checking: "Checking the link…",
  },
  errors: {
    password_too_short: "Password must be at least {{min}} characters.",
    reset_link_invalid: "This reset link is invalid or has expired.",
    too_many_requests: "Too many attempts. Please try again in a few minutes.",
    no_game_for_poster: "No contest available to generate a poster.",
    contact_send_failed: "The message could not be sent. Please try again in a moment.",
    contact_required: "Name, email and message are required.",
    merchant_login_missing: "This advertiser has no login yet: enter an email and a password to create it.",
  },
  merchant_editor: {
    load_error: "Unable to load this game",
    back: "Back to dashboard",
    eyebrow: "Edit my game",
    notice:
      "You can edit the title, description, messages, image and dates. Prizes, odds and stock are locked: contact your administrator to adjust them.",
    texts: "Texts",
    visuals: "Visuals",
    scheduling: "Schedule",
    rules: "Game rules",
    prizes_locked: "Prizes (locked)",
    prizes_locked_hint:
      "To keep the draw fair, only your administrator can change prizes, odds or stock.",
    segment_color_hint: "Segment colour",
    prize_instructions_badge: "Email — info about this prize",
    prize_instructions_placeholder: "E.g. Valid on Tuesdays only, 2pm to 5pm, in store.",
    prize_instructions_hint: "Shown in the win email under the code, in addition to the game's general message.",
    prize_popup_badge: "Extra details",
    prize_popup_placeholder: "E.g. Come to our store with your email.",
    prize_popup_hint: "Shown in the on-screen win pop-up, right after the game.",
  },
  game_editor: {
    field_title: "Title",
    field_description: "Description",
    field_win_message: "Winning message (victory screen)",
    field_lose_message: "Losing message",
    field_win_email: "Custom message in the winner's email",
    field_win_email_hint: "E.g. “Show this email to your waiter to collect your prize”.",
    usable_from_mode: "Prize can be collected",
    usable_from_date: "Exact date",
    rules_content: "Rules text",
    rules_hint: "This text is shown on the public page when a player clicks “Rules”.",
    preview_title: "Game title",
    preview_continue: "Continue →",
    preview_prize: "Winning prize",
    preview_win: "Congratulations!",
    new_game_language: "Players' language",
  },
  poster: {
    social_header: "Share on social media",
    social_square: "Instagram post",
    social_story: "Story / Reel",
    social_hint: "Optimised for Instagram, Facebook Stories and TikTok.",
    social_story_done: "Instagram story downloaded!",
    social_square_done: "Instagram post downloaded!",
    in_progress: "live",
    cta_default: "SCAN THIS QR CODE TO WIN A GIFT",
    image_alt: "QRISE poster",
    lang_note: "The poster text is written in the game's language ({{lang}}).",
    err_billing: "AI image billing limit reached. Please contact your administrator.",
    err_quota: "AI image quota exhausted. Please contact your administrator.",
    err_rate_limit: "Too many requests to the AI service, please try again in a minute.",
    err_api_key: "The AI image service is not configured. Please contact your administrator.",
    err_timeout: "Poster generation took too long. Please try again.",
    err_failed: "Poster generation failed. Please try again.",
  },
  dashboard: {
    merchant_updated: "Merchant updated",
    edit_merchant: "Edit merchant",
    new_password: "New password",
    optional: "optional — leave empty to keep the current one",
    kpi_wins: "Wins",
    conversion_rate: "Conversion rate",
    wins_over_plays: "wins / plays (all-time)",
    chart_per_game_bars: "Plays · leads · wins per game",
    chart_plays_donut: "Play distribution",
    chart_conversion_per_game: "Conversion rate by game",
    chart_hourly: "Activity by hour (last 30 days)",
    chart_prizes: "Most won prizes",
    no_prizes_yet: "No prize won yet",
    merchant_language: "Advertiser space language",
    merchant_language_hint: "Used for their space, their emails and as the default language of their new games.",
    send_welcome: "Send the welcome email (login link, without the password)",
    welcome_sent: "Welcome email sent to {{email}}",
    welcome_failed: "Account created, but the welcome email could not be sent",
    no_login_hint: "This advertiser has no login yet. Enter an email and a password to create their access.",
    merchant_name_ph: "Name",
    merchant_company_ph: "Company",
    merchant_email_ph: "Email",
    merchant_password_ph: "Password",
  },
  contact_admin: {
    cta: "Contact admin",
    title: "Contact your administrator",
    subtitle: "Your request goes straight to the QRISE team. We reply within 24 business hours.",
    reason: "Reason",
    reasons: {
      new_game: "New game",
      edit_game: "Edit a game",
      technical: "Technical issue",
      billing: "Billing",
      other: "Other",
    },
    game_type: "Which game would you like to run?",
    game_type_undecided: "Not sure yet — advise me",
    game_concerned: "Game concerned",
    game_none: "No specific game",
    name: "Your name",
    email: "Reply email",
    company: "Brand / company",
    phone: "Phone",
    optional: "Optional",
    message: "Your message",
    message_ph: "Describe your request…",
    send: "Send request",
    sending: "Sending…",
    cancel: "Cancel",
    success_title: "Request sent!",
    success_body: "Thanks {{name}}, the QRISE team will reply very soon at {{email}}.",
    close: "Close",
    required: "Name, email and message are required.",
    types: {
      spin_wheel: "Wheel of fortune",
      scratch_card: "Scratch card",
      card_game: "Memory card game",
      slot_machine: "Slot machine",
      lottery_draw: "Prize draw",
      prediction_match: "Sports predictions",
      guess_price: "Guess the price",
      quiz: "Quiz",
      ninja: "Product ninja",
      blow_candles: "Blow the candles",
      flappy: "Flappy mascot",
      mystery_box: "Mystery box",
      registration_gift: "Sign-up gift",
      advent_calendar: "Advent calendar",
    },
  },
};

const fr = {
  common: { saved: "Enregistré", winning: "Gagnant", losing: "Perdant" },
  auth: {
    welcome_toast: "Bienvenue, {{name}}",
    forgot_link: "Mot de passe oublié ?",
    forgot_title: "Mot de passe oublié ?",
    forgot_subtitle: "Saisissez votre email de connexion. Nous vous envoyons un lien pour choisir un nouveau mot de passe.",
    forgot_submit: "Envoyer le lien",
    forgot_sending: "Envoi…",
    forgot_sent_title: "Consultez votre boîte mail",
    forgot_sent_body: "Si un compte existe pour {{email}}, un lien de réinitialisation vient de partir. Il est valable 1 heure.",
    back_to_login: "Retour à la connexion",
    reset_title: "Choisissez un nouveau mot de passe",
    reset_subtitle: "8 caractères minimum. Il vous servira à vous connecter à votre espace QRISE.",
    new_password: "Nouveau mot de passe",
    confirm_password: "Confirmez le mot de passe",
    reset_submit: "Enregistrer mon nouveau mot de passe",
    reset_saving: "Enregistrement…",
    reset_done_title: "Mot de passe modifié",
    reset_done_body: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
    go_to_login: "Se connecter",
    password_mismatch: "Les mots de passe ne correspondent pas.",
    reset_invalid_title: "Lien invalide ou expiré",
    reset_invalid_body: "Ce lien de réinitialisation a déjà été utilisé ou a expiré. Demandez-en un nouveau ci-dessous.",
    request_new_link: "Demander un nouveau lien",
    checking: "Vérification du lien…",
  },
  errors: {
    password_too_short: "Le mot de passe doit contenir au moins {{min}} caractères.",
    reset_link_invalid: "Ce lien de réinitialisation est invalide ou a expiré.",
    too_many_requests: "Trop de tentatives. Réessayez dans quelques minutes.",
    no_game_for_poster: "Aucun jeu-concours disponible pour générer une affiche.",
    contact_send_failed: "Le message n'a pas pu être envoyé. Réessayez dans un instant.",
    contact_required: "Le nom, l'email et le message sont obligatoires.",
    merchant_login_missing: "Cet annonceur n'a pas encore d'accès : renseignez un email et un mot de passe pour le créer.",
  },
  merchant_editor: {
    load_error: "Impossible de charger ce jeu",
    back: "Retour au tableau de bord",
    eyebrow: "Éditer mon jeu",
    notice:
      "Vous pouvez modifier le titre, la description, les messages, l'image et les dates. Les lots, probabilités et stocks sont verrouillés : contactez votre administrateur pour les ajuster.",
    texts: "Textes",
    visuals: "Visuels",
    scheduling: "Planning",
    rules: "Règlement du jeu",
    prizes_locked: "Lots (verrouillés)",
    prizes_locked_hint:
      "Pour garantir l'équité du tirage, seul votre administrateur peut modifier les lots, les probabilités ou le stock.",
    segment_color_hint: "Couleur du segment",
    prize_instructions_badge: "Email — info sur ce lot",
    prize_instructions_placeholder: "Ex : Valable uniquement le mardi de 14h à 17h, sur place.",
    prize_instructions_hint: "Apparaît dans l'email de gain sous le code, en plus du message global du jeu.",
    prize_popup_badge: "Détails supplémentaires",
    prize_popup_placeholder: "Ex : Rendez-vous en boutique avec votre email.",
    prize_popup_hint: "S'affiche dans la pop-up de gain à l'écran, immédiatement après la partie.",
  },
  game_editor: {
    field_title: "Titre",
    field_description: "Description",
    field_win_message: "Message gagnant (écran de victoire)",
    field_lose_message: "Message perdant",
    field_win_email: "Message personnalisé dans l'email du gagnant",
    field_win_email_hint: "Ex. « Présentez cet email à votre serveur pour récupérer votre lot ».",
    usable_from_mode: "Lot récupérable",
    usable_from_date: "Date précise",
    rules_content: "Texte du règlement",
    rules_hint: "Ce texte est affiché sur la page publique quand un joueur clique sur « Règlement ».",
    preview_title: "Titre du jeu",
    preview_continue: "Continuer →",
    preview_prize: "Lot gagnant",
    preview_win: "Félicitations !",
    new_game_language: "Langue des joueurs",
  },
  poster: {
    social_header: "Publier sur les réseaux",
    social_square: "Post Instagram",
    social_story: "Story / Reel",
    social_hint: "Format optimisé pour Instagram, Facebook Stories et TikTok.",
    social_story_done: "Story Instagram téléchargée !",
    social_square_done: "Post Instagram téléchargé !",
    in_progress: "en cours",
    cta_default: "FLASHEZ CE QR CODE POUR GAGNER UN CADEAU",
    image_alt: "Affiche QRISE",
    lang_note: "Les textes de l'affiche sont rédigés dans la langue du jeu ({{lang}}).",
    err_billing: "Limite de facturation du service d'images IA atteinte. Contactez votre administrateur.",
    err_quota: "Quota du service d'images IA épuisé. Contactez votre administrateur.",
    err_rate_limit: "Trop de demandes au service IA, réessayez dans 1 minute.",
    err_api_key: "Le service d'images IA n'est pas configuré. Contactez votre administrateur.",
    err_timeout: "La génération de l'affiche a pris trop de temps. Réessayez.",
    err_failed: "La génération de l'affiche a échoué. Réessayez.",
  },
  dashboard: {
    merchant_updated: "Marchand mis à jour",
    edit_merchant: "Modifier le marchand",
    new_password: "Nouveau mot de passe",
    optional: "optionnel — laisser vide pour conserver",
    kpi_wins: "Gains",
    conversion_rate: "Taux de conversion",
    wins_over_plays: "gains / parties (depuis le début)",
    chart_per_game_bars: "Parties · leads · gains par jeu",
    chart_plays_donut: "Répartition des parties",
    chart_conversion_per_game: "Taux de conversion par jeu",
    chart_hourly: "Activité par heure (30 derniers jours)",
    chart_prizes: "Lots les plus gagnés",
    no_prizes_yet: "Aucun lot gagné pour le moment",
    merchant_language: "Langue de l'espace annonceur",
    merchant_language_hint: "Utilisée pour son espace, ses emails et comme langue par défaut de ses nouveaux jeux.",
    send_welcome: "Envoyer l'email de bienvenue (lien de connexion, sans le mot de passe)",
    welcome_sent: "Email de bienvenue envoyé à {{email}}",
    welcome_failed: "Compte créé, mais l'email de bienvenue n'a pas pu être envoyé",
    no_login_hint: "Cet annonceur n'a pas encore d'accès. Renseignez un email et un mot de passe pour le créer.",
    merchant_name_ph: "Nom",
    merchant_company_ph: "Société",
    merchant_email_ph: "Email",
    merchant_password_ph: "Mot de passe",
  },
  contact_admin: {
    cta: "Contacter l'admin",
    title: "Contacter votre administrateur",
    subtitle: "Votre demande arrive directement à l'équipe QRISE. Réponse sous 24 h ouvrées.",
    reason: "Motif",
    reasons: {
      new_game: "Nouveau jeu",
      edit_game: "Modifier un jeu",
      technical: "Problème technique",
      billing: "Facturation",
      other: "Autre",
    },
    game_type: "Quel jeu souhaitez-vous faire ?",
    game_type_undecided: "Je ne sais pas encore — conseillez-moi",
    game_concerned: "Jeu concerné",
    game_none: "Aucun jeu en particulier",
    name: "Votre nom",
    email: "Email de réponse",
    company: "Enseigne / société",
    phone: "Téléphone",
    optional: "Facultatif",
    message: "Votre message",
    message_ph: "Décrivez votre demande…",
    send: "Envoyer la demande",
    sending: "Envoi…",
    cancel: "Annuler",
    success_title: "Demande envoyée !",
    success_body: "Merci {{name}}, l'équipe QRISE vous répond très vite à l'adresse {{email}}.",
    close: "Fermer",
    required: "Le nom, l'email et le message sont obligatoires.",
    types: {
      spin_wheel: "Roue de la fortune",
      scratch_card: "Carte à gratter",
      card_game: "Jeu de cartes (Memory)",
      slot_machine: "Machine à sous",
      lottery_draw: "Tirage au sort",
      prediction_match: "Pronostic sportif",
      guess_price: "Le Juste Prix",
      quiz: "Quiz",
      ninja: "Ninja produits",
      blow_candles: "Souffle les bougies",
      flappy: "Flappy Mascotte",
      mystery_box: "Boîte mystère",
      registration_gift: "Inscription cadeau",
      advent_calendar: "Calendrier de l'Avent",
    },
  },
};

const accountResources = { en, fr };
export default accountResources;
